import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Box, Button } from '@mui/material';

export const CARD_DESK_CRASH_MESSAGE = 'La Mesa de cruces tuvo un error. Recargá la página; si sigue, avisá.';

/** Cualquier tira de 12 dígitos o más (con espacios o guiones): un número de tarjeta no sale en el log. */
const hideCardNumbers = (text: string): string => text.replace(/\d(?:[ -]?\d){11,}/g, '[número oculto]');

interface Props {
	children: ReactNode;
	/** Lo que se ve si algo de adentro se rompe al dibujarse. */
	message?: string;
	/** Con el botón "Recargar". La página del cliente no lo lleva: el token del link no sobrevive a una recarga. */
	reload?: boolean;
	/** Lo que se ve en lugar del aviso (en "Códigos de comercio y cuotas", la tabla fija). */
	fallback?: ReactNode;
}

interface State {
	failed: boolean;
}

/**
 * Un error de render en la mesa (o en la página de pago del cliente) queda acá: se ve un aviso en su lugar y el menú y
 * el resto del Power siguen andando. React ya deja el error en la consola tal cual; acá no se repite: se suma solo el
 * mensaje, sin números de tarjeta, y dónde pasó.
 */
export class CardDeskErrorBoundary extends Component<Props, State> {
	state: State = { failed: false };

	static getDerivedStateFromError(): State {
		return { failed: true };
	}

	componentDidCatch(error: unknown, info: ErrorInfo): void {
		const message = error instanceof Error ? error.message : String(error);
		console.error('[CardDeskErrorBoundary]', hideCardNumbers(message), info.componentStack);
	}

	render(): ReactNode {
		const { children, message = CARD_DESK_CRASH_MESSAGE, reload = true, fallback } = this.props;
		if (!this.state.failed) return children;
		if (fallback !== undefined) return fallback;
		return (
			<Box sx={{ px: 2, py: 3 }}>
				<Alert
					severity="error"
					action={
						reload && (
							<Button color="inherit" size="small" onClick={() => window.location.reload()}>
								Recargar
							</Button>
						)
					}
				>
					{message}
				</Alert>
			</Box>
		);
	}
}
